/**
 * Trilho (desktop), à esquerda do mapa: marca, um botão por painel registrado
 * e "Sobre" no rodapé.
 *
 * Cada botão é o INTERRUPTOR da camada: um clique liga, outro desliga — o
 * ícone acende na cor da camada e o rótulo fica branco enquanto ela está no
 * mapa. Uma seta estreita na borda direita abre (ou recolhe) o painel com as
 * opções e a legenda; enquanto o painel mostra aquela camada, uma barra fina
 * marca o botão à esquerda. Ligar uma camada com o painel aberto faz o painel
 * passar a mostrá-la; com o painel recolhido, nada muda de lugar.
 *
 * Painel sem camada própria (só configurações) faz o botão inteiro abrir o
 * painel, como antes. Abrir um painel recolhe o inspetor (exclusão mútua).
 *
 * No "Próximos dias" (18/10/2026) o topo do trilho passa a ser o das camadas da
 * previsão, e as observadas ficam encolhidas em "Observado", logo abaixo
 * (`previsao/BotoesDaPrevisao`). O que não cabe na altura rola por dentro.
 */
import clsx from 'clsx';
import { ChevronRight, Info } from 'lucide-react';
import { useId } from 'react';
import { ObservadoNoRail, PrevisaoNoRail } from '../previsao/BotoesDaPrevisao';
import { usePrevisaoStore } from '../previsao/store';
import { useUiStore } from '../state/ui';
import { BrandMark } from './Brand';
import { marcarVisto, useEhNovidade } from './novidade';
import { getPanels, type PanelDef } from './panels';
import { DEFAULT_TONE, TONE_BAR, TONE_ICON } from './tone';

/**
 * Corpo do rótulo conforme o comprimento. Os cortes vêm de medir o que cabe
 * nos 46 px livres do botão (60 px menos a seta), descontando o respiro.
 */
function tamanhoDoRotulo(label: string): string {
  // Várias palavras que não cabem numa linha quebram em duas (ver `quebraEmDuas`).
  if (quebraEmDuas(label)) return 'text-[8.5px]';
  if (label.length > 10) return 'text-[7.5px]';
  if (label.length > 7) return 'text-[8.5px]';
  return 'text-[9.5px]';
}

/**
 * "Qualidade do ar" encolhido numa linha vira "Qualidade …": com mais de uma
 * palavra, o rótulo longo quebra entre elas, em duas linhas.
 */
export function quebraEmDuas(label: string): boolean {
  return label.length > 12 && label.includes(' ');
}

function StatusDots(props: { warning: boolean; novidade: boolean }) {
  if (props.warning) {
    return (
      <span
        className="absolute top-1.5 left-1.5 size-1.5 rounded-full bg-yellow-300"
        role="img"
        aria-label="dados indisponíveis"
        title="Dados indisponíveis no momento"
      />
    );
  }
  // O amarelo do aviso tem prioridade: problema no dado importa mais que
  // novidade, e dois pontos no mesmo canto não se distinguiriam.
  if (props.novidade) {
    return (
      <span
        className="absolute top-1.5 left-1.5 size-1.5 rounded-full bg-mata-400 ring-2 ring-ink-900"
        role="img"
        aria-label="camada nova"
        title="Camada nova"
      />
    );
  }
  return null;
}

function RailItem(props: { def: PanelDef }) {
  const { def } = props;
  const tab = useUiStore((s) => s.tab);
  const open = useUiStore((s) => s.sidebarOpen);
  const togglePanel = useUiStore((s) => s.togglePanel);
  const followPanel = useUiStore((s) => s.followPanel);
  const showing = open && tab === def.id;
  const warning = def.useWarning?.() ?? false;
  // `def` é fixo por instância (chave = id), então a ordem dos hooks não muda.
  const on = def.layer?.useOn() ?? false;
  const novidade = useEhNovidade(def.id, def.novoAte);
  const tone = def.tone ?? DEFAULT_TONE;
  const Icon = def.icon;

  const openOptions = () => {
    marcarVisto(def.id);
    togglePanel(def.id);
  };
  const toggleLayer = () => {
    if (!def.layer) return openOptions();
    marcarVisto(def.id);
    def.layer.setOn(!on);
    if (!on) followPanel(def.id);
  };

  const state = def.layer ? (on ? 'camada ligada' : 'camada desligada') : undefined;
  return (
    <div className="relative h-[52px] w-[60px] shrink-0" data-rail-item={def.id}>
      <button
        type="button"
        onClick={toggleLayer}
        aria-pressed={def.layer ? on : undefined}
        aria-label={
          def.layer
            ? `${def.label}: ${on ? 'desligar' : 'ligar'} camada`
            : `${showing ? 'Recolher' : 'Abrir'} o painel ${def.label}`
        }
        title={state ? `${def.label} — ${state}` : def.label}
        className={clsx(
          'flex h-full w-full flex-col items-center justify-center gap-0.5 rounded-lg pr-3.5 font-medium tracking-tight transition-colors',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400',
          on ? 'bg-ink-700/70 text-white hover:bg-ink-700' : 'text-ink-300 hover:bg-ink-700/50 hover:text-white',
        )}
      >
        <Icon className={clsx('size-5', on ? TONE_ICON[tone] : 'text-ink-400')} aria-hidden />
        {/* O rótulo encolhe em vez de quebrar: "Pluviômetros" é uma palavra só
            e a quebra cairia no meio dela. */}
        <span
          className={clsx(
            'max-w-full',
            quebraEmDuas(def.label) ? 'line-clamp-2 text-center leading-[1.05]' : 'truncate',
            tamanhoDoRotulo(def.label),
          )}
        >
          {def.label}
        </span>
        <StatusDots warning={warning} novidade={novidade} />
      </button>
      {def.layer && (
        <button
          type="button"
          onClick={openOptions}
          aria-pressed={showing}
          aria-label={showing ? `Recolher o painel ${def.label}` : `Abrir o painel ${def.label}`}
          title={showing ? 'Recolher as opções' : `Opções e legenda: ${def.label}`}
          className={clsx(
            'absolute inset-y-0 right-0 flex w-3.5 items-center justify-center rounded-r-lg transition-colors',
            'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400',
            showing ? 'bg-ink-600 text-white' : 'text-ink-500 hover:bg-ink-600 hover:text-white',
          )}
        >
          <ChevronRight className={clsx('size-3 transition-transform', showing && 'rotate-180')} aria-hidden />
        </button>
      )}
      {showing && <span className={clsx('absolute inset-y-2.5 left-0 w-0.5 rounded-full', TONE_BAR[tone])} aria-hidden />}
    </div>
  );
}

export function Rail() {
  const set = useUiStore((s) => s.set);
  const modo = usePrevisaoStore((s) => s.modo);
  const observadoAberto = usePrevisaoStore((s) => s.observadoAberto);
  const listaId = useId();
  return (
    <nav
      className="z-20 flex w-16 flex-col items-center gap-0.5 border-r border-ink-700/80 bg-ink-900 px-0.5 pt-2.5 pb-2"
      style={{ gridColumn: 1, gridRow: '1 / span 2' }}
      aria-label="Camadas"
    >
      <div className="mb-2 flex flex-col items-center gap-0.5" title="AmaView — Amazônia em dados abertos">
        <BrandMark className="size-8" />
        <span className="text-[10px] font-semibold tracking-tight text-white">AmaView</span>
      </div>
      <div className="flex min-h-0 w-full flex-1 flex-col items-center gap-0.5 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {modo && <PrevisaoNoRail />}
        {modo && <ObservadoNoRail controla={listaId} />}
        {(!modo || observadoAberto) && (
          <div id={listaId} className="flex flex-col items-center gap-0.5" role="group" aria-label={modo ? 'Camadas observadas' : undefined}>
            {getPanels().map((def) => (
              <RailItem key={def.id} def={def} />
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => set({ aboutOpen: true })}
        className="mt-1 flex h-11 w-12 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg text-[10px] text-ink-300 transition-colors hover:bg-ink-700/70 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400"
        aria-label="Sobre o AmaView"
      >
        <Info className="size-5" aria-hidden />
        Sobre
      </button>
    </nav>
  );
}
